'use client';

/**
 * Backmix dialog (project: per-use attribution).
 *
 * Two modes, modeled on the "Remix with" dialog:
 *
 *  • propose (remixer)  → describe the feature you're offering back to the
 *    original app and which markers represent it; sends a proposal (a discussion
 *    post) that notifies the original author.
 *  • accept (original author, opened from the notification) → hand a coding agent
 *    a ready prompt to MERGE the proposed feature into your app and INSERT FAIR
 *    attribution markers crediting the contributor, then publish (the publish
 *    carries `backmixProposalId` so the merged feature keeps crediting them).
 *
 * Fairness of the markers/weights is deferred to the two humans + AI via the
 * linked discussion thread.
 */

import { createPortal } from 'react-dom';
import { useT } from '@/components/i18n-provider';
import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, GitMerge, Loader2, Send, X } from 'lucide-react';

export interface BackmixProposal {
  proposalPostId: string;
  /** The fork (remix) app name. */
  forkName: string;
  message: string | null;
  markerIds: string[];
  contributorName?: string;
  discussionPostId?: string;
}

interface BackmixDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'propose' | 'accept';
  /** The app in context (the fork for propose, the original for accept). */
  appName: string;
  /** propose: send the proposal. */
  onPropose?: (args: { message: string; markerIds: string[] }) => Promise<void>;
  /** accept: the incoming proposal. */
  proposal?: BackmixProposal;
  /** accept: drive the in-app factory agent on the original app. */
  onMergeWithFactory?: (prompt: string) => void;
}

function buildAcceptPrompt(p: BackmixProposal, appName: string): string {
  const who = p.contributorName ? `@${p.contributorName}` : 'the contributor';
  return [
    `Read BACKMIX.md, then merge the feature proposed by ${who} (from "${p.forkName}") into this app ("${appName}").`,
    p.message ? `\nProposal notes:\n${p.message}` : '',
    `\nDiff the fork against this app to judge what ${who} actually contributed, then FAIRLY PLACE attribution marks across the contributed code so its usage credits them:`,
    `- mark the feature's code paths with host.attribution.mark('${who}/...') / span('${who}/...'), proportional to the real work.`,
    `- if the fork already carries ${who} marks, keep them; never relabel them to the owner. Glue code you write gets ${who}'s handle too.`,
    `\nThen publish. From then on, runtime usage of those marked paths credits ${who} automatically (no provenance bookkeeping).`,
  ].filter(Boolean).join('\n');
}

export function BackmixDialog(props: BackmixDialogProps) {
  const t = useT();
  const { open, onClose, mode, appName, onPropose, proposal, onMergeWithFactory } = props;
  const [message, setMessage] = useState('');
  const [markersText, setMarkersText] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) { setMessage(''); setMarkersText(''); setBusy(false); setCopied(false); }
  }, [open]);

  const acceptPrompt = useMemo(
    () => (mode === 'accept' && proposal ? buildAcceptPrompt(proposal, appName) : ''),
    [mode, proposal, appName],
  );

  if (!open || typeof document === 'undefined') return null;

  const submitPropose = async () => {
    if (!onPropose || busy) return;
    setBusy(true);
    try {
      const markerIds = markersText.split(',').map((s) => s.trim()).filter(Boolean);
      await onPropose({ message: message.trim(), markerIds });
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(acceptPrompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4" onMouseDown={onClose}>
      <div
        className="w-full max-w-lg rounded-xl border border-white/10 bg-neutral-900 p-5 text-sm text-neutral-100 shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 font-medium">
            <GitMerge className="h-4 w-4 text-fuchsia-400" />
            {mode === 'propose' ? t('workspace.backmix.proposeToOriginal') : t('workspace.backmix.mergeProposal')}
          </div>
          <button onClick={onClose} className="rounded p-1 text-neutral-400 hover:bg-white/5 hover:text-white" aria-label={t('workspace.canvas.node.close')}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {mode === 'propose' ? (
          <div className="space-y-3">
            <p className="text-neutral-400">
              Offer your feature back to the original app. The author gets a notification and can merge it; usage of the
              merged feature will credit you.
            </p>
            <label className="block">
              <span className="mb-1 block text-xs text-neutral-400">{t('workspace.backmix.whatDoes')}</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder={t('workspace.backmix.describePlaceholder')}
                className="w-full resize-none rounded-lg border border-white/10 bg-neutral-950 p-2 text-sm outline-none focus:border-fuchsia-500/60"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-neutral-400">Feature markers (optional, comma-separated)</span>
              <input
                value={markersText}
                onChange={(e) => setMarkersText(e.target.value)}
                placeholder="e.g. export_pdf, smart_compose"
                className="w-full rounded-lg border border-white/10 bg-neutral-950 p-2 text-sm outline-none focus:border-fuchsia-500/60"
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="rounded-lg px-3 py-1.5 text-neutral-300 hover:bg-white/5">Cancel</button>
              <button
                onClick={submitPropose}
                disabled={busy || message.trim().length === 0}
                className="inline-flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-3 py-1.5 font-medium text-white hover:bg-fuchsia-500 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send proposal
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-neutral-400">
              {t('workspace.backmix.proposedFrom', { who: proposal?.contributorName ? `@${proposal.contributorName}` : t('workspace.backmix.aContributor') })}
              {' '}<span className="text-neutral-200">{proposal?.forkName}</span>{t('workspace.backmix.handToAgent')}
            </p>
            <pre className="max-h-56 overflow-auto whitespace-pre-wrap rounded-lg border border-white/10 bg-neutral-950 p-3 text-xs text-neutral-300">
              {acceptPrompt}
            </pre>
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              {proposal?.discussionPostId && (
                <a
                  href={`/p/${proposal.discussionPostId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-lg px-3 py-1.5 text-neutral-300 hover:bg-white/5"
                >
                  {t('workspace.backmix.openDiscussion')}
                </a>
              )}
              <button onClick={copyPrompt} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-neutral-200 hover:bg-white/5">
                {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                Copy prompt
              </button>
              {onMergeWithFactory && (
                <button
                  onClick={() => onMergeWithFactory(acceptPrompt)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-fuchsia-600 px-3 py-1.5 font-medium text-white hover:bg-fuchsia-500"
                >
                  <GitMerge className="h-4 w-4" />
                  {t('workspace.backmix.mergeWithFactory')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
